// Pure, browser-local snippet builders for the widget/embed generators:
// cookie-consent banner, social share buttons, and HTML tables. No DOM access,
// no external deps, so these stay safe to import from client components.

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Encode a value for a single-quoted JavaScript string literal.
function jsString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\r?\n/g, ' ');
}

// --- Cookie consent banner -------------------------------------------------

export type CookieConsentOptions = {
  message: string;
  acceptLabel: string;
  declineLabel: string;
  showDecline: boolean;
  position: 'top' | 'bottom';
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
  buttonTextColor: string;
  linkText: string;
  linkUrl: string;
  storageKey: string;
};

export const defaultCookieConsentOptions: CookieConsentOptions = {
  message: 'We use cookies to improve your experience and analyze site traffic.',
  acceptLabel: 'Accept',
  declineLabel: 'Decline',
  showDecline: true,
  position: 'bottom',
  backgroundColor: '#1f2937',
  textColor: '#f9fafb',
  buttonColor: '#4f46e5',
  buttonTextColor: '#ffffff',
  linkText: 'Learn more',
  linkUrl: 'https://example.com/privacy',
  storageKey: 'cookie-consent',
};

export function buildCookieConsentSnippet(options: CookieConsentOptions): string {
  const key = (options.storageKey || 'cookie-consent').trim() || 'cookie-consent';
  const posStyle =
    options.position === 'top' ? 'top:0;left:0;right:0;' : 'bottom:0;left:0;right:0;';
  const link =
    options.linkText.trim() && options.linkUrl.trim()
      ? `<a href="${escapeHtml(options.linkUrl.trim())}" style="color:${escapeHtml(
          options.buttonColor,
        )};text-decoration:underline;margin-left:6px;">${escapeHtml(options.linkText.trim())}</a>`
      : '';
  const declineBtn = options.showDecline
    ? `<button type="button" data-cc-decline style="background:transparent;color:${escapeHtml(
        options.textColor,
      )};border:1px solid ${escapeHtml(
        options.textColor,
      )};border-radius:6px;padding:8px 16px;font:inherit;cursor:pointer;">${escapeHtml(
        options.declineLabel.trim() || 'Decline',
      )}</button>`
    : '';

  return `<!-- Cookie consent banner -->
<div id="cookie-consent-banner" style="display:none;position:fixed;${posStyle}z-index:9999;background:${escapeHtml(
    options.backgroundColor,
  )};color:${escapeHtml(
    options.textColor,
  )};padding:16px 20px;box-shadow:0 -2px 12px rgba(0,0,0,0.15);font-family:system-ui,-apple-system,sans-serif;font-size:14px;">
  <div style="max-width:1100px;margin:0 auto;display:flex;flex-wrap:wrap;gap:12px;align-items:center;justify-content:space-between;">
    <p style="margin:0;flex:1 1 280px;line-height:1.5;">${escapeHtml(
      options.message.trim(),
    )}${link}</p>
    <div style="display:flex;gap:8px;flex-shrink:0;">
      ${declineBtn}
      <button type="button" data-cc-accept style="background:${escapeHtml(
        options.buttonColor,
      )};color:${escapeHtml(
        options.buttonTextColor,
      )};border:none;border-radius:6px;padding:8px 16px;font:inherit;cursor:pointer;">${escapeHtml(
        options.acceptLabel.trim() || 'Accept',
      )}</button>
    </div>
  </div>
</div>
<script>
(function () {
  var KEY = '${jsString(key)}';
  var banner = document.getElementById('cookie-consent-banner');
  if (!banner) return;
  function set(value) {
    try { localStorage.setItem(KEY, value); } catch (e) {}
    banner.style.display = 'none';
  }
  var stored = null;
  try { stored = localStorage.getItem(KEY); } catch (e) {}
  if (!stored) { banner.style.display = 'block'; }
  var accept = banner.querySelector('[data-cc-accept]');
  var decline = banner.querySelector('[data-cc-decline]');
  if (accept) accept.addEventListener('click', function () { set('accepted'); });
  if (decline) decline.addEventListener('click', function () { set('declined'); });
})();
</script>`;
}

// --- Social share buttons --------------------------------------------------

export type ShareNetwork = 'twitter' | 'facebook' | 'linkedin' | 'whatsapp' | 'reddit' | 'email';

export const shareNetworkLabels: Record<ShareNetwork, string> = {
  twitter: 'X / Twitter',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  whatsapp: 'WhatsApp',
  reddit: 'Reddit',
  email: 'Email',
};

const shareNetworkColors: Record<ShareNetwork, string> = {
  twitter: '#000000',
  facebook: '#1877f2',
  linkedin: '#0a66c2',
  whatsapp: '#25d366',
  reddit: '#ff4500',
  email: '#6b7280',
};

export function buildShareUrl(network: ShareNetwork, url: string, title: string): string {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  switch (network) {
    case 'twitter':
      return `https://twitter.com/intent/tweet?url=${u}&text=${t}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    case 'whatsapp':
      return `https://api.whatsapp.com/send?text=${t}%20${u}`;
    case 'reddit':
      return `https://www.reddit.com/submit?url=${u}&title=${t}`;
    case 'email':
      return `mailto:?subject=${t}&body=${u}`;
    default:
      return url;
  }
}

export type ShareOptions = {
  url: string;
  title: string;
  networks: ShareNetwork[];
};

export function buildShareButtonsSnippet(options: ShareOptions): string {
  const buttons = options.networks
    .map((network) => {
      const href = buildShareUrl(network, options.url, options.title);
      const label = shareNetworkLabels[network];
      const color = shareNetworkColors[network];
      const target = network === 'email' ? '' : ' target="_blank" rel="noopener noreferrer"';
      return `  <a href="${escapeHtml(
        href,
      )}"${target} style="display:inline-block;background:${color};color:#ffffff;text-decoration:none;border-radius:6px;padding:8px 14px;font:14px system-ui,-apple-system,sans-serif;">Share on ${escapeHtml(
        label,
      )}</a>`;
    })
    .join('\n');

  return `<!-- Social share buttons -->
<div class="social-share" style="display:flex;flex-wrap:wrap;gap:8px;">
${buttons}
</div>`;
}

// --- HTML table ------------------------------------------------------------

export type TableOptions = {
  rows: number;
  cols: number;
  headerRow: boolean;
  striped: boolean;
  bordered: boolean;
  inlineCss: boolean;
  cells: string[][];
};

export function makeEmptyCells(rows: number, cols: number, existing?: string[][]): string[][] {
  const grid: string[][] = [];
  for (let r = 0; r < rows; r += 1) {
    const row: string[] = [];
    for (let c = 0; c < cols; c += 1) {
      row.push(existing?.[r]?.[c] ?? '');
    }
    grid.push(row);
  }
  return grid;
}

export function buildTableSnippet(options: TableOptions): string {
  const { rows, cols, headerRow, striped, bordered, inlineCss, cells } = options;
  const indent = '  ';
  const lines: string[] = [];

  const tableAttrs = inlineCss
    ? ` style="border-collapse:collapse;width:100%;font-family:system-ui,-apple-system,sans-serif;font-size:14px;${
        bordered ? 'border:1px solid #d1d5db;' : ''
      }"`
    : ' class="generated-table"';
  lines.push(`<table${tableAttrs}>`);

  const cellPad = bordered ? 'padding:8px 12px;border:1px solid #d1d5db;' : 'padding:8px 12px;';

  for (let r = 0; r < rows; r += 1) {
    const isHeader = headerRow && r === 0;
    const tag = isHeader ? 'th' : 'td';
    const rowStriped = inlineCss && striped && !isHeader && r % 2 === 0;
    const rowStyle = isHeader
      ? inlineCss
        ? ` style="background:#f3f4f6;text-align:left;"`
        : ''
      : rowStriped
        ? ` style="background:#f9fafb;"`
        : '';
    lines.push(`${indent}<tr${rowStyle}>`);
    for (let c = 0; c < cols; c += 1) {
      const value = escapeHtml((cells[r]?.[c] ?? '').trim());
      const cellStyle = inlineCss ? ` style="${cellPad}"` : '';
      lines.push(`${indent}${indent}<${tag}${cellStyle}>${value}</${tag}>`);
    }
    lines.push(`${indent}</tr>`);
  }

  lines.push('</table>');

  if (!inlineCss) {
    const css: string[] = [
      '<style>',
      '.generated-table { border-collapse: collapse; width: 100%; font-family: system-ui, -apple-system, sans-serif; font-size: 14px; }',
      `.generated-table th, .generated-table td { padding: 8px 12px;${
        bordered ? ' border: 1px solid #d1d5db;' : ''
      } }`,
      '.generated-table th { background: #f3f4f6; text-align: left; }',
    ];
    if (striped) {
      css.push('.generated-table tbody tr:nth-child(even) { background: #f9fafb; }');
    }
    if (bordered) {
      css.push('.generated-table { border: 1px solid #d1d5db; }');
    }
    css.push('</style>');
    return `${css.join('\n')}\n${lines.join('\n')}`;
  }

  return lines.join('\n');
}
